import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useStreamClient } from "@/context/stream-context";
import { useMe } from "@/context/user-context";
import { reportStreamChatError } from "@/lib/stream-chat-errors";
import { routes } from "@/routes";

import { useToast } from "./use-toast";

export function useOpenMessageThread() {
  const streamClient = useStreamClient();
  const { streamUser, user } = useMe();
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { toast } = useToast();

  return useCallback((memberId: string) => {
    const showUnavailableToast = () => toast({
      title: t("error-occured"),
      description: t("chat-unavailable"),
    });

    if (!streamClient) {
      if (streamUser.token || streamUser.unavailable) {
        showUnavailableToast();
      }
      return;
    }

    void (async () => {
      try {
        const channel = streamClient.channel("messaging", {
          members: [user.id, memberId],
        });
        const createdChannel = await channel.create();

        if (createdChannel.membership?.archived_at) {
          await channel.unarchive();
        }

        navigate(`${routes.messages}?channelId=${createdChannel.channel.id}`);
      }
      catch (error) {
        reportStreamChatError(error, "open-message-thread");
        showUnavailableToast();
      }
    })();
  }, [navigate, streamClient, streamUser.token, streamUser.unavailable, t, toast, user.id]);
}
